import { useEffect, useRef, useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { useIntl } from "react-intl";
import Disconnect from "@/assets/images/disconnected-error.svg";

type ServiceUnavailableProps = {
  /** Refetches the failed boot-critical queries (RTK Query `refetch`). */
  onRetry: () => void;
};

// Exponential-ish backoff schedule (seconds) for the auto-retry.
const RETRY_DELAYS = [5, 10, 20, 30];

/**
 * Recoverable replacement for the old "Oops → auto-logout in 9s" page on
 * TRANSIENT failures (backend redeploy, network blip). The session and the
 * Redux state stay intact; we simply refetch on a backoff schedule (and on
 * demand) until the backend answers again — at which point the parent's
 * query succeeds and this screen unmounts by itself.
 */
const ServiceUnavailable = ({ onRetry }: ServiceUnavailableProps) => {
  const { locale } = useIntl();
  const attemptRef = useRef(0);
  const [countdown, setCountdown] = useState(RETRY_DELAYS[0]);
  const [retrying, setRetrying] = useState(false);

  const fr = locale !== "en-US";

  useEffect(() => {
    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev > 1) return prev - 1;
        // Fire an auto-retry and arm the next window.
        setRetrying(true);
        onRetry();
        setTimeout(() => setRetrying(false), 1_500);
        attemptRef.current = Math.min(attemptRef.current + 1, RETRY_DELAYS.length - 1);
        return RETRY_DELAYS[attemptRef.current];
      });
    }, 1_000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleManualRetry = () => {
    setRetrying(true);
    onRetry();
    setTimeout(() => setRetrying(false), 1_500);
    setCountdown(RETRY_DELAYS[Math.min(attemptRef.current, RETRY_DELAYS.length - 1)]);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        gap: 3,
        px: 3,
        textAlign: "center",
      }}
    >
      <img src={Disconnect} style={{ maxHeight: "40vh" }} />
      <Box>
        <Typography sx={{ fontSize: 28, fontWeight: 700, mb: 1 }}>
          {fr ? "Connexion au serveur perdue" : "Connection to the server lost"}
        </Typography>
        <Typography sx={{ fontSize: 15, color: "text.secondary", maxWidth: 480 }}>
          {fr
            ? `Le service est momentanément indisponible — votre session est conservée. Nouvelle tentative automatique dans ${countdown}s.`
            : `The service is temporarily unreachable — your session is preserved. Retrying automatically in ${countdown}s.`}
        </Typography>
      </Box>
      <Button
        variant="contained"
        onClick={handleManualRetry}
        disabled={retrying}
        startIcon={retrying ? <CircularProgress size={16} color="inherit" /> : undefined}
        sx={{ textTransform: "none", borderRadius: 2, px: 4 }}
      >
        {retrying ? (fr ? "Tentative…" : "Retrying…") : fr ? "Réessayer" : "Retry"}
      </Button>
    </Box>
  );
};

export default ServiceUnavailable;
